import React from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Badge      from "@react/shared/ui/Badge"
import EmptyState from "@react/shared/ui/EmptyState"
import { formatMoney } from "./StatsStrip"

const GRID_COLUMNS = "140px minmax(220px, 1.7fr) minmax(180px, 1fr) 160px 140px"

const STATUS_TONE = {
  draft:     "slate",
  active:    "success",
  on_hold:   "warning",
  reception: "violet",
  completed: "blue",
  cancelled: "neutral",
}

const SortHeader = ({ label, column, order, direction, onSortChange, align = "left" }) => {
  const active        = order === column
  const nextDirection = active && direction === "asc" ? "desc" : "asc"
  const justify       = align === "right" ? "justify-end" : align === "center" ? "justify-center" : "justify-start"
  return (
    <button
      type      = "button"
      onClick   = {() => onSortChange(column, nextDirection)}
      className = {`flex items-center gap-1 ${justify} uppercase tracking-wider text-[11px] font-medium ${active ? "text-slate-700" : "text-slate-500 hover:text-slate-700"}`}
    >
      {label}
      {active && <FontAwesomeIcon icon={direction === "asc" ? "arrow-up" : "arrow-down"} className="text-[9px]" />}
    </button>
  )
}

const ProjectRow = ({ project }) => (
  <div
    role      = "row"
    className = "group grid items-center gap-x-3 px-4 py-3 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 cursor-pointer"
    style     = {{ gridTemplateColumns: GRID_COLUMNS }}
    onClick   = {() => { window.location.href = project.panorama_url }}
  >
    <div>
      <Badge tone="neutral" variant="soft" size="md" className="font-mono max-w-full truncate">
        {project.code}
      </Badge>
    </div>

    <div className="min-w-0 text-sm text-slate-900 font-medium truncate" title={project.name}>
      {project.name || "—"}
    </div>

    <div className="min-w-0 text-sm text-slate-700 truncate" title={project.client_name}>
      {project.client_name || "—"}
      {project.partner_name && (
        <div className="text-xs text-slate-500 truncate" title={project.partner_name}>{project.partner_name}</div>
      )}
    </div>

    <div className="text-right tabular-nums font-semibold text-slate-900 whitespace-nowrap">
      {formatMoney(project.initial_budget_cents, project.currency_symbol)}
    </div>

    <div>
      <Badge tone={STATUS_TONE[project.status] || "neutral"} variant="soft" size="xs">
        {project.status_label}
      </Badge>
    </div>
  </div>
)

const HeaderRow = ({ order, direction, onSortChange }) => (
  <div
    role      = "row"
    className = "grid items-center gap-x-3 px-4 py-3 border-b border-slate-200 bg-slate-50/50"
    style     = {{ gridTemplateColumns: GRID_COLUMNS }}
  >
    <SortHeader label={I18n.t("projects.index.table.code")}   column="projects.code"                 order={order} direction={direction} onSortChange={onSortChange} />
    <SortHeader label={I18n.t("projects.index.table.name")}   column="projects.name"                 order={order} direction={direction} onSortChange={onSortChange} />
    <SortHeader label={I18n.t("projects.index.table.client")} column="clients.display_name"          order={order} direction={direction} onSortChange={onSortChange} />
    <SortHeader label={I18n.t("projects.index.table.budget")} column="projects.initial_budget_cents" order={order} direction={direction} onSortChange={onSortChange} align="right" />
    <SortHeader label={I18n.t("projects.index.table.status")} column="projects.status"               order={order} direction={direction} onSortChange={onSortChange} />
  </div>
)

const ProjectsTable = ({ projects, order, direction, onSortChange }) => (
  <div className="overflow-x-auto" role="table">
    <HeaderRow order={order} direction={direction} onSortChange={onSortChange} />
    {projects.map(project => (
      <ProjectRow key={project.id} project={project} />
    ))}
    {projects.length === 0 && (
      <EmptyState size="lg">{I18n.t("projects.index.table.empty")}</EmptyState>
    )}
  </div>
)

export default ProjectsTable
